import React, {useEffect, useState} from 'react'
import _ from "lodash";
import {getSingleQuote, updateSingleQuote} from "../common/services/quote-data-service";
import {DEFAULT_SETTINGS, SAVE_QUOTE_TIME_DELAY} from "../common/constants/quote.constant";
// import dynamic from "next/dynamic";
import ShowLink from "../common/ui/show-link";
import EditQuoteDetail from "./edit-quote-detail";
import {COLOR_LIST} from "../common/constants/color.constant";
import {processColorDetails} from "../common/utils/color-util";
import QuoteKonvaEditor from "./quote-konva-editor";
import {useParams} from 'react-router-dom';
import WebFontLoader from "webfontloader";
import {FONT_NAME_LIST} from "../common/constants/designer.constant";

const colorMap = _.keyBy(COLOR_LIST, 'colorKey');

const QuoteDesigner = () => {
    const {quoteId} = useParams();
    const [initialStep, setInitialStep] = useState(true);
    const [quote, setQuote] = useState(undefined);
    let [changesCount, setChangesCount] = useState(0);
    const [selectedDisplayText, setSelectedDisplayText] = useState(undefined);
    const [isSettingSelected, setIsSettingSelected] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        if (quoteId && initialStep) {
            getSingleQuote(quoteId)
                .then(response => {
                    const quoteDetail = _.cloneDeep(response.data());
                    quoteDetail.id = quoteDetail.id || quoteId;

                    if (!quoteDetail.settings) {
                        quoteDetail.settings = DEFAULT_SETTINGS;
                    }

                    setQuote(quoteDetail);
                });
        }
    }, [quoteId, initialStep]);

    useEffect(() => {
        incrementChangesCount();
    }, [quote]);

    useEffect(() => {
        const timeoutId =
            setTimeout(async () => {

                if (changesCount > 0) {
                    resetChangesCount();
                    saveQuoteChanges();
                }

            }, SAVE_QUOTE_TIME_DELAY);

        return () => clearTimeout(timeoutId);
    }, [changesCount]);

    const saveQuoteChanges = async () => {
        const existingQuote = {...quote};

        if (!_.isEmpty(existingQuote)) {
            console.log('existingQuote: *************** ', existingQuote);
            await updateSingleQuote(existingQuote);
        }

    }

    const incrementChangesCount = () => {
        setChangesCount(++changesCount);
    }

    const resetChangesCount = () => {
        setChangesCount(0);
    }

    useEffect(() => {
        WebFontLoader.load({
            custom: {
                families: FONT_NAME_LIST
            }
        });
    }, [initialStep]);

    const initSettings = () => {
        const existingQuote = {...quote};
        const settings = existingQuote.settings || {};

        existingQuote.settings = {
            fontSize: settings.fontSize || DEFAULT_SETTINGS.fontSize,
            fontFamily: settings.fontFamily || DEFAULT_SETTINGS.fontFamily,
            colorKey: settings.colorKey || DEFAULT_SETTINGS.colorKey,
            colorCodeKey: settings.colorCodeKey || DEFAULT_SETTINGS.colorCodeKey,
            hexCode: settings.hexCode || DEFAULT_SETTINGS.hexCode,
            width: parseInt(settings.width) || DEFAULT_SETTINGS.width,
            height: parseInt(settings.height) || DEFAULT_SETTINGS.height,
        };

        setQuote(existingQuote);
    }

    const handleQuoteSettingsChanges = async (event) => {
        const {name, value, type} = event.target;
        const existingQuote = {...quote};
        const settings = existingQuote.settings || DEFAULT_SETTINGS;

        if (type === 'number' || name === 'fontSize') {
            settings[name] = parseInt(value);
        } else if (name && value) {
            settings[name] = value;
        }

        processColorDetails(settings);

        existingQuote.settings = settings;
        processDisplayTextItems(existingQuote, name, true);

        setQuote(existingQuote);
    }

    const processDisplayTextItems = (existingQuote, fieldName?, isSettingsChanged?) => {
        if (_.isEmpty(existingQuote.settings)) {
            existingQuote.settings = {};
        }

        _.forEach(existingQuote.displayTextItems, (displayTextItem) => {
            if (fieldName === 'fontSize') {
                displayTextItem.fontSize = existingQuote.settings.fontSize || displayTextItem.fontSize || '';
            } else if (fieldName === 'fontFamily') {
                displayTextItem.fontFamily = existingQuote.settings.fontFamily || displayTextItem.fontFamily || '';
            } else if (fieldName === 'colorKey') {
                displayTextItem.colorKey = existingQuote.settings.colorKey || displayTextItem.colorKey || '';
            } else if (fieldName === 'colorCodeKey') {
                displayTextItem.colorCodeKey = existingQuote.settings.colorCodeKey || displayTextItem.colorCodeKey || '';
            } else if (fieldName === 'strokeWidth') {
                if (isSettingsChanged) {
                    displayTextItem.strokeWidth = existingQuote.settings.strokeWidth || 0
                } else {
                    displayTextItem.strokeWidth = displayTextItem.strokeWidth || existingQuote.settings.strokeWidth || 0;
                }
            } else if (fieldName === 'strokeColorKey') {
                displayTextItem.strokeColorKey = existingQuote.settings.strokeColorKey || displayTextItem.strokeColorKey || '';
            } else if (fieldName === 'strokeColorCodeKey') {
                displayTextItem.strokeColorCodeKey = existingQuote.settings.strokeColorCodeKey || displayTextItem.strokeColorCodeKey || '';
            } else if (fieldName === 'shadowOffsetX') {
                if (isSettingsChanged) {
                    displayTextItem.shadowOffsetX = existingQuote.settings.shadowOffsetX || 0
                } else {
                    displayTextItem.shadowOffsetX = displayTextItem.shadowOffsetX || existingQuote.settings.shadowOffsetX || 0;
                }
            } else if (fieldName === 'shadowOffsetY') {
                if (isSettingsChanged) {
                    displayTextItem.shadowOffsetY = existingQuote.settings.shadowOffsetY || 0
                } else {
                    displayTextItem.shadowOffsetY = displayTextItem.shadowOffsetY || existingQuote.settings.shadowOffsetY || 0;
                }
            } else if (fieldName === 'shadowBlur') {
                if (isSettingsChanged) {
                    displayTextItem.shadowBlur = existingQuote.settings.shadowBlur || 0
                } else {
                    displayTextItem.shadowBlur = displayTextItem.shadowBlur || existingQuote.settings.shadowBlur || 0;
                }
            } else if (fieldName === 'shadowColorKey') {
                displayTextItem.shadowColorKey = existingQuote.settings.shadowColorKey || displayTextItem.shadowColorKey || '';
            } else if (fieldName === 'shadowColorCodeKey') {
                displayTextItem.shadowColorCodeKey = existingQuote.settings.shadowColorCodeKey || displayTextItem.shadowColorCodeKey || '';
            }

            processColorDetails(displayTextItem);
        });
    }

    const handleQuoteDisplayTextChanges = (event) => {
        const existingQuote = {...quote};
        if (event && event.target) {
            const {name, value} = event.target;
            existingQuote[name] = value;
        }

        const changedDisplayTextItems = _.split(existingQuote.displayText, '\n');
        const tempDisplayTextItems = [];

        const settings = existingQuote.settings || DEFAULT_SETTINGS;
        const currentDisplayTextItems = existingQuote.displayTextItems || [];

        _.forEach(changedDisplayTextItems, (text, index) => {
            text = text.trim();

            let x = 100;
            let y = 100 * (index + 1);
            let itemKey = index;
            let fontSize = settings.fontSize;
            let fontFamily = settings.fontFamily;
            let colorKey = settings.colorKey;
            let colorCodeKey = settings.colorCodeKey;
            let hexCode = settings.hexCode;
            let strokeColorKey = settings.strokeColorKey;
            let strokeColorCodeKey = settings.strokeColorCodeKey;
            let stroke = settings.stroke;
            let strokeWidth = settings.strokeWidth;
            let lineJoin = settings.lineJoin || DEFAULT_SETTINGS.lineJoin;
            let fillAfterStrokeEnabled = settings.fillAfterStrokeEnabled || DEFAULT_SETTINGS.fillAfterStrokeEnabled;
            let shadowColorKey = settings.shadowColorKey;
            let shadowColorCodeKey = settings.shadowColorCodeKey;
            let shadowOffsetX = settings.shadowOffsetX;
            let shadowOffsetY = settings.shadowOffsetY;
            let shadowBlur = settings.shadowBlur;

            if (currentDisplayTextItems[index]) {
                fontSize = currentDisplayTextItems[index].fontSize;
                fontFamily = currentDisplayTextItems[index].fontFamily;
                itemKey = currentDisplayTextItems[index].itemKey;
                colorKey = currentDisplayTextItems[index].colorKey;
                colorCodeKey = currentDisplayTextItems[index].colorCodeKey;
                x = currentDisplayTextItems[index].x || x;
                y = currentDisplayTextItems[index].y || y;
                strokeColorKey = currentDisplayTextItems[index].strokeColorKey;
                strokeColorCodeKey = currentDisplayTextItems[index].strokeColorCodeKey;
                stroke = currentDisplayTextItems[index].stroke || stroke;
                strokeWidth = currentDisplayTextItems[index].strokeWidth || strokeWidth;
                shadowColorKey = currentDisplayTextItems[index].shadowColorKey || shadowColorKey;
                shadowColorCodeKey = currentDisplayTextItems[index].shadowColorCodeKey || shadowColorCodeKey;
                shadowOffsetX = currentDisplayTextItems[index].shadowOffsetX || shadowOffsetX;
                shadowOffsetY = currentDisplayTextItems[index].shadowOffsetY || shadowOffsetY;
                shadowBlur = currentDisplayTextItems[index].shadowBlur || shadowBlur;
            }

            const tempTextItm = {
                itemKey: itemKey || index,
                x: x,
                y: y,
                text: text,
                fontSize: fontSize || '',
                fontFamily: fontFamily || '',
                colorKey: colorKey || '',
                colorCodeKey: colorCodeKey || '',
                hexCode: hexCode || '',
                strokeColorKey: strokeColorKey || '',
                strokeColorCodeKey: strokeColorCodeKey || '',
                stroke: stroke || '',
                strokeWidth: strokeWidth || '',
                fillAfterStrokeEnabled: fillAfterStrokeEnabled,
                lineJoin: lineJoin,
                shadowColorKey: shadowColorKey || '',
                shadowColorCodeKey: shadowColorCodeKey || '',
                shadowOffsetX: shadowOffsetX || '',
                shadowOffsetY: shadowOffsetY || '',
                shadowBlur: shadowBlur || '',
            }

            processColorDetails(tempTextItm);

            tempDisplayTextItems.push(tempTextItm);
        });

        existingQuote.displayTextItems = tempDisplayTextItems;
        setQuote(existingQuote);
    };

    const handleSelectedDisplayTextTab = (textItem) => {
        if (textItem) {
            setSelectedDisplayText(textItem);
            setIsSettingSelected(false);
        } else {
            setSelectedDisplayText(undefined);
            setIsSettingSelected(true);
        }

    }

    return (
        <>

            <div className="page-container dashboard-container flex justify-start">

                <div className="editor-menu-container flex flex-wrap content-start justify-start p-4 overflow-x-scroll">

                    <div className="justify-start items-start w-full">
                        <div className="flex justify-start items-start w-full">
                            <ShowLink label="Quotes"
                                      url="/quotes"/>
                            <div className="float-right">
                                {changesCount > 0 &&
                                    <div
                                        className="ml-4 rounded-full p-1 h-6 w-6 bg-gray-500 text-white flex items-center justify-center">
                                        <span className="text-sm">{changesCount}</span>
                                    </div>
                                }
                            </div>
                        </div>
                        <div className="flex justify-start items-start w-full">
                            <div className="title-label mt-4 flex justify-start items-start">
                                {quote?.startText}
                            </div>
                        </div>
                    </div>

                    <div className="justify-start items-start w-full">
                        {quote?.settings &&
                            <EditQuoteDetail
                                quote={quote}
                                setQuote={setQuote}
                                selectedDisplayText={selectedDisplayText}
                                handleSelectedDisplayTextTab={handleSelectedDisplayTextTab}
                                handleQuoteSettingsChanges={handleQuoteSettingsChanges}
                                handleQuoteDisplayTextChanges={handleQuoteDisplayTextChanges}
                                setSelectedDisplayText={setSelectedDisplayText}
                                isSettingSelected={isSettingSelected}
                                setIsSettingSelected={setIsSettingSelected}
                                setSelectedImage={setSelectedImage}
                            />
                        }
                    </div>
                </div>

                <div className="editor-konva-container items-start justify-start md:col-span-2 pb-16 mr-6 my-8">
                    <div className="quote-konva-editor quote-konva-editor-container">
                        {quote?.settings &&
                            <QuoteKonvaEditor
                                quote={quote}
                                setQuote={setQuote}
                                selectedDisplayText={selectedDisplayText}
                                handleSelectedDisplayTextTab={handleSelectedDisplayTextTab}
                                currentImage={selectedImage}
                            />
                        }
                    </div>
                </div>

            </div>

        </>
    )
}

export default QuoteDesigner
